import { QuestionSequenceName } from 'api/foodaid/questions';
import { SegmentationContextType } from 'contexts/segmentation';
import intersection from 'lodash/intersection';
import { useMemo } from 'react';

const SEQUENCE_NAMES = Object.values(QuestionSequenceName);
const EXCEPTIONAL_ROUTES = [
  '/collections/fur-hunde-nassfutter',
  '/collections/fur-hunde-trockenfutter',
  '/collections/fur-hunde-barf-frische-menus',
  '/collections/fur-hunde-adult',
];

interface UseSequenceNameProps {
  isReady: boolean;
  petType: string;
  routePaths: (string | null)[];
  segmentation?: SegmentationContextType['segmentation'];
  sequenceId?: string;
}

/**
 * Hook to determine the question sequence name to use based on segmentation, route, and other factors.
 */
const useSequenceName = ({
  isReady,
  petType,
  routePaths,
  segmentation,
  sequenceId,
}: UseSequenceNameProps): string | null => {
  return useMemo(() => {
    if (isReady && petType && !sequenceId) {
      const defaultSegmentation = QuestionSequenceName.Foodaid;
      if (!segmentation) {
        return defaultSegmentation;
      }

      const _segmentation =
        typeof segmentation === 'string'
          ? segmentation
          : segmentation.needSegmentation && segmentation.needSegmentation[0];

      // Retrieve sequence from segmentation
      if (
        _segmentation &&
        SEQUENCE_NAMES.includes(segmentation as QuestionSequenceName)
      ) {
        return _segmentation;
      }

      // Reset sequence when last path is exceptional routes
      // and when petType is cat until segmentation for cat is available
      if (
        (_segmentation &&
          intersection(EXCEPTIONAL_ROUTES, routePaths).length > 0) ||
        petType === 'cat'
      ) {
        return defaultSegmentation;
      }

      return defaultSegmentation;
    }
    return null;
  }, [isReady, petType, segmentation, routePaths, sequenceId]);
};

export default useSequenceName;
